import { useEffect, useState } from "react";
import { ExternalLink, RefreshCw, Search, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const money = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const dateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString("en-IN");
};

const statusStyle = (status) => {
  const s = String(status || "").toLowerCase();
  if (["approved", "paid", "captured"].includes(s))
    return "bg-emerald-400/10 text-emerald-300";
  if (["pending", "submitted"].includes(s))
    return "bg-amber-400/10 text-amber-300";
  if (["rejected", "failed", "cancelled"].includes(s))
    return "bg-red-400/10 text-red-300";
  return "bg-white/5 text-white/60";
};

export default function InstallmentHistory() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/installment-history", {
        params: { q: query || undefined, status, limit: 2000 },
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load installment history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status]);

  const totals = rows.reduce((acc, row) => {
    const s = String(row.status || "").toLowerCase();
    const amount = Number(row.amount || 0);
    if (["approved", "paid", "captured"].includes(s)) acc.paid += amount;
    else if (s === "pending") acc.pending += amount;
    else if (["rejected", "failed"].includes(s)) acc.failed += amount;
    return acc;
  }, { paid: 0, pending: 0, failed: 0 });

  const openInvoice = (row) => {
    if (!row.mandate_id || !row.installment_id) return;
    window.open(
      `/mandate-invoice/${encodeURIComponent(row.mandate_id)}/${encodeURIComponent(row.installment_id)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="space-y-6 text-white">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Installment History</h1>
          <p className="mt-1 text-sm text-white/45">
            Installment transactions, invoices, statuses and activity logs
          </p>
        </div>
        <button onClick={load} className="rounded-xl border border-white/10 p-2.5">
          <RefreshCw size={17} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          ["Approved / Paid", totals.paid, "text-emerald-300"],
          ["Pending", totals.pending, "text-amber-300"],
          ["Rejected / Failed", totals.failed, "text-red-300"],
        ].map(([label, amount, color]) => (
          <div key={label} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-xs text-white/45">{label}</div>
            <div className={`mt-1 text-xl font-semibold ${color}`}>{money(amount)}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3">
          <Search size={16} className="text-white/40" />
          <input
            className="w-full bg-transparent py-3 text-sm outline-none"
            placeholder="Search invoice, customer, mandate, installment or UTR"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-xl border border-white/10 bg-[#111] px-3 py-2 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved / Paid</option>
          <option value="rejected">Rejected</option>
          <option value="failed">Failed</option>
        </select>
        <button onClick={load} className="rounded-xl border border-white/10 px-4 py-2 text-sm">
          Search
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/10">
        <div className="hidden grid-cols-6 gap-3 border-b border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-white/45 lg:grid">
          <span>Invoice / Customer</span><span>Mandate</span><span>Amount</span>
          <span>Method</span><span>Status</span><span>Activity</span>
        </div>

        {rows.map((row) => {
          const isExpanded = !!expanded[row.installment_id];
          return (
            <div key={row.installment_id} className="border-b border-white/5 last:border-0">
              <div className="grid gap-3 px-4 py-4 text-sm lg:grid-cols-6 lg:items-center">
                <div>
                  <div className="font-medium break-all">{row.invoice_no || "Invoice pending"}</div>
                  <div className="text-xs text-white/50">
                    {row.customer_name || "Customer"} · {row.customer_email || "—"}
                  </div>
                  <button onClick={() => openInvoice(row)}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-[#fbce18] hover:underline">
                    <FileText size={13} /> View invoice <ExternalLink size={12} />
                  </button>
                </div>
                <div>
                  <div>{row.mandate_title || "Mandate"}</div>
                  <div className="text-xs text-white/40 break-all">{row.mandate_id}</div>
                  {row.category && <div className="text-xs text-white/40">{row.category}</div>}
                </div>
                <div>
                  <div className="font-medium">{money(row.amount)}</div>
                  <div className="text-xs text-white/40">{dateTime(row.created_at)}</div>
                </div>
                <div className="text-white/70">{row.method || "—"}</div>
                <div>
                  <span className={`inline-flex rounded-full px-2 py-1 text-xs ${statusStyle(row.status)}`}>
                    {row.status || "unknown"}
                  </span>
                  {row.payment_reference && <div className="mt-1 text-xs text-white/40">Ref: {row.payment_reference}</div>}
                  {row.utr && <div className="mt-1 text-xs text-white/40">UTR: {row.utr}</div>}
                </div>
                <div>
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [row.installment_id]: !p[row.installment_id] }))}
                    className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 text-xs"
                  >
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {isExpanded ? "Hide logs" : `View logs (${row.events?.length || 0})`}
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className="mx-4 mb-4 rounded-xl border border-white/10 bg-black/20 p-4">
                  <div className="mb-3 text-sm font-medium">Installment activity</div>
                  <div className="space-y-3">
                    {(row.events || []).length ? row.events.map((event, i) => (
                      <div key={`${row.installment_id}-${i}`} className="flex gap-3 text-sm">
                        <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#fbce18]" />
                        <div>
                          <div>{event.event}</div>
                          <div className="text-xs text-white/45">{dateTime(event.at)}</div>
                          {event.by && <div className="text-xs text-white/45">Reviewed by: {event.by}</div>}
                          {event.reason && <div className="text-xs text-red-300">Reason: {event.reason}</div>}
                        </div>
                      </div>
                    )) : <div className="text-xs text-white/45">No timestamped events recorded for this installment.</div>}
                  </div>
                  <div className="mt-4 grid gap-2 text-xs text-white/50 sm:grid-cols-2">
                    <div>Installment ID: {row.installment_id}</div>
                    <div>Submitted: {dateTime(row.submitted_at)}</div>
                    <div>Reviewed: {dateTime(row.reviewed_at)}</div>
                    <div>Reviewed by: {row.reviewed_by || "—"}</div>
                    {row.rejection_reason && <div className="text-red-300">Rejection reason: {row.rejection_reason}</div>}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {!rows.length && (
          <div className="p-8 text-center text-sm text-white/40">
            {loading ? "Loading history…" : "No installment history found."}
          </div>
        )}
      </div>
    </div>
  );
}
